import { Room, RoomEvent, Track, VideoPresets } from 'livekit-client';

/**
 * A call's media, through the LiveKit media server.
 *
 * Tracks come out through onRemoteTrack as four slots (audio, camera, screen,
 * screenAudio) that the tiles and audio sinks read, keyed by seat id - which
 * is the LiveKit identity the server put in the token.
 *
 * Each device holds one connection, to the server; nobody offers, answers or
 * restarts ICE. The server decides who may be in the room (it only gives
 * tokens to seats, and removes anyone else).
 *
 * The local tracks are still ours: the microphone (or its RNNoise version),
 * the camera and the screen are captured by CallContext exactly as before and
 * handed in through setLocal, which publishes them. LiveKit never opens a
 * device or stops a track itself.
 */

const SLOT_FOR_SOURCE = {
    [Track.Source.Microphone]: 'audio',
    [Track.Source.Camera]: 'camera',
    [Track.Source.ScreenShare]: 'screen',
    [Track.Source.ScreenShareAudio]: 'screenAudio'
};

const SOURCE_FOR_SLOT = {
    audio: Track.Source.Microphone,
    camera: Track.Source.Camera,
    screen: Track.Source.ScreenShare
};

/*
 * Camera: 720p, plus one lower layer the server can send instead to anyone on
 * a poor connection. Two levels, not three, to keep the server's CPU down -
 * see CallServerMigration.md. A shared screen is one layer, favouring
 * sharpness over frame rate: a slide at a few frames a second is readable.
 */
const PUBLISH_OPTIONS = {
    audio: { dtx: true, red: true },
    camera: {
        simulcast: true,
        videoEncoding: VideoPresets.h720.encoding,
        videoSimulcastLayers: [VideoPresets.h360]
    },
    screen: {
        simulcast: false,
        // `screenShareEncoding`, not `videoEncoding`: LiveKit reads a screen
        // track's settings from here and ignores the camera's. Left out, it
        // sent 2.5 Mbps at 1080p.
        screenShareEncoding: { maxBitrate: 1_500_000, maxFramerate: 15 },
        degradationPreference: 'maintain-resolution'
    }
};

// `<userId>~2` is somebody's second device; both belong to `<userId>`.
const ownerOf = (identity) => String(identity || '').replace(/~2$/, '');

export const createMediaRoom = ({ onRemoteTrack, onPeerState, onDisconnected }) => {
    const room = new Room({
        // Our own <video> and <audio> elements show the tracks (see CallTile
        // and CallAudioSinks), so the SDK cannot see which are on screen;
        // adaptive stream would read that as "nothing is" and pause them.
        adaptiveStream: false,
        // Stop sending a camera layer nobody is receiving.
        dynacast: true,
        // CallContext owns every device and stops every track itself.
        stopLocalTrackOnUnpublish: false
    });

    let selfOwner = '';
    let connectedAs = null;
    let connecting = null;
    let closed = false;

    // What we want to send, whether or not we are connected yet.
    let local = { audio: null, camera: null, screen: null };
    // slot -> LocalTrackPublication
    const published = new Map();
    // One change to what we publish at a time, in order.
    let queue = Promise.resolve();

    const isOwnDevice = (participant) => ownerOf(participant.identity) === selfOwner;

    // Our other device is in the room too; hearing it is hearing ourselves.
    const subscribeTo = (participant) => {
        if (isOwnDevice(participant)) return;
        for (const publication of participant.trackPublications.values()) {
            publication.setSubscribed(true);
        }
    };

    const remotes = () => [...room.remoteParticipants.values()].filter((p) => !isOwnDevice(p));

    /*
     * A closed room that comes back by itself: the SDK can finish a reconnect
     * it had already started after disconnect(), and the room then holds the
     * seat's identity - so the next real connection is removed as the
     * duplicate. Anything it does after closeAll() is shut down at once.
     */
    const shutIfClosed = () => {
        if (!closed) return false;
        room.disconnect().catch(() => { /* already gone */ });
        return true;
    };

    room
        .on(RoomEvent.Connected, shutIfClosed)
        .on(RoomEvent.SignalConnected, shutIfClosed)
        .on(RoomEvent.ParticipantConnected, (participant) => {
            if (shutIfClosed() || isOwnDevice(participant)) return;
            onPeerState(participant.identity, 'connected');
            subscribeTo(participant);
        })
        .on(RoomEvent.TrackPublished, (publication, participant) => {
            if (shutIfClosed()) return;
            if (!isOwnDevice(participant)) publication.setSubscribed(true);
        })
        .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
            const slot = SLOT_FOR_SOURCE[publication.source];
            if (!slot || isOwnDevice(participant)) return;
            onRemoteTrack(participant.identity, { slot, stream: new MediaStream([track.mediaStreamTrack]) });
        })
        .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
            const slot = SLOT_FOR_SOURCE[publication.source];
            if (slot) onRemoteTrack(participant.identity, { slot, stream: undefined });
        })
        .on(RoomEvent.ParticipantDisconnected, (participant) => {
            if (!isOwnDevice(participant)) onPeerState(participant.identity, 'closed');
        })
        .on(RoomEvent.Reconnecting, () => {
            if (shutIfClosed()) return;
            for (const p of remotes()) onPeerState(p.identity, 'connecting');
        })
        .on(RoomEvent.Reconnected, () => {
            if (shutIfClosed()) return;
            for (const p of remotes()) onPeerState(p.identity, 'connected');
        })
        .on(RoomEvent.Disconnected, () => {
            // Ours, from closeAll, is not news. Anything else is the call's
            // media gone after the SDK has already tried to get it back.
            if (closed) return;
            connectedAs = null;
            onDisconnected?.('Lost the connection to the call.');
        });

    /** Make what is published match `local`. */
    const sync = () => {
        queue = queue.then(async () => {
            if (!connectedAs || closed) return;
            const me = room.localParticipant;

            for (const slot of Object.keys(SOURCE_FOR_SLOT)) {
                const want = local[slot] || null;
                const have = published.get(slot) || null;

                if (!want) {
                    if (have?.track) await me.unpublishTrack(have.track, false);
                    published.delete(slot);
                    continue;
                }
                if (have?.track) {
                    // Same slot, different track: a new microphone, or noise
                    // suppression switched on or off. Swapped in place.
                    if (have.track.mediaStreamTrack !== want) await have.track.replaceTrack(want, true);
                    continue;
                }
                const publication = await me.publishTrack(want, {
                    source: SOURCE_FOR_SLOT[slot],
                    ...PUBLISH_OPTIONS[slot]
                });
                published.set(slot, publication);
            }
        }).catch((err) => {
            console.error('[call] could not publish to the media server:', err.message);
        });
        return queue;
    };

    return {
        kind: 'sfu',

        /**
         * Join the call's room. Called again for the same seat (a fresh token
         * after a socket reconnect) it does nothing: the media connection is
         * the SDK's to keep alive, and it has its own token refresh.
         */
        async connect(url, token, identity) {
            if (closed) return;
            if (connectedAs === identity) return;
            if (connecting) return connecting;

            selfOwner = ownerOf(identity);
            connecting = (async () => {
                await room.connect(url, token, { autoSubscribe: false });
                connectedAs = identity;
                for (const participant of remotes()) {
                    onPeerState(participant.identity, 'connected');
                    subscribeTo(participant);
                }
                await sync();
            })();
            try {
                await connecting;
            } finally {
                connecting = null;
            }
        },

        /** Change what we are sending. Published once connected. */
        async setLocal(next) {
            local = { ...local, ...next };
            await sync();
        },

        /** Who is talking - seat id -> 0..1, for the active-speaker ring. */
        levels() {
            const out = {};
            for (const participant of remotes()) out[participant.identity] = participant.audioLevel || 0;
            return out;
        },

        ids() {
            return remotes().map((p) => p.identity);
        },

        isConnected() {
            return !closed && Boolean(connectedAs) && room.state === 'connected';
        },

        closeAll() {
            closed = true;
            published.clear();
            room.disconnect().catch(() => { /* already gone */ });
        }
    };
};
